import React, { useState } from 'react';
import { FiX, FiUser, FiLock, FiMail } from 'react-icons/fi';
import './AuthModal.css';
import { setToken, getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';

const AuthModal = ({ isOpen, onClose, onLoginSuccess, initialIsLogin = true }) => {
  const [isLogin, setIsLogin] = useState(initialIsLogin);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setIsLogin(initialIsLogin);
      setError('');
      setFormData({ username: '', email: '', password: '' });
    }
  }, [isOpen, initialIsLogin]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        const formDataToSend = new URLSearchParams();
        formDataToSend.append('username', formData.username);
        formDataToSend.append('password', formData.password);

        const response = await fetch(getApiEndpoint('/api/auth/login'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formDataToSend
        });

        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          throw new Error(text || 'Login failed');
        }

        if (!response.ok) {
          throw new Error(data.detail || data.message || 'Login failed');
        }

        console.log('Login successful, token received:', data.access_token ? 'Yes' : 'No');
        setToken(data.access_token);
        console.log('Token saved to localStorage');
        // Small delay to ensure token is saved before checking auth
        setTimeout(() => {
          onLoginSuccess();
          onClose();
        }, 50);
      } else {
        // Register
        console.log('Starting registration...', formData.username);
        const response = await fetch(getApiEndpoint('/api/auth/register'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: formData.username,
            email: formData.email,
            password: formData.password
          })
        });

        console.log('Registration response status:', response.status);

        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
          console.log('Registration response data:', data);
        } else {
          const text = await response.text();
          console.error('Registration non-JSON response:', text);
          throw new Error(text || 'Registration failed');
        }

        if (!response.ok) {
          console.error('Registration failed:', data);
          throw new Error(data.detail || data.message || 'Registration failed');
        }

        console.log('Registration successful! Attempting auto-login...');

        // Auto-login after registration
        const loginFormData = new URLSearchParams();
        loginFormData.append('username', formData.username);
        loginFormData.append('password', formData.password);

        console.log('Calling login API...');
        const loginResponse = await fetch(getApiEndpoint('/api/auth/login'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: loginFormData
        });

        console.log('Login response status:', loginResponse.status);

        let loginData;
        const loginContentType = loginResponse.headers.get('content-type');
        if (loginContentType && loginContentType.includes('application/json')) {
          loginData = await loginResponse.json();
          console.log('Login response data:', loginData);
        } else {
          const text = await loginResponse.text();
          console.error('Login non-JSON response:', text);
          throw new Error(text || 'Registration successful but login failed');
        }

        if (!loginResponse.ok) {
          console.error('Auto-login failed:', loginData);
          throw new Error(loginData.detail || loginData.message || 'Registration successful but login failed');
        }

        console.log('Auto-login successful! Token:', loginData.access_token ? 'received' : 'missing');
        setToken(loginData.access_token);
        onLoginSuccess();
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay ph-no-capture" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          <FiX />
        </button>

        <div className="auth-modal-header">
          <h2>{isLogin ? 'Login' : 'Register'}</h2>
          <p>{isLogin ? 'Welcome back!' : 'Create a new account'}</p>
        </div>

        {error && (
          <div className="auth-modal-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-modal-form">
          <div className="auth-form-group">
            <FiUser className="auth-form-icon" />
            <input
              type="text"
              name="username"
              placeholder="Username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>

          {!isLogin && (
            <div className="auth-form-group">
              <FiMail className="auth-form-icon" />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <div className="auth-form-group">
            <FiLock className="auth-form-icon" />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <button type="submit" className="auth-submit-button" disabled={loading}>
            {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Register')}
          </button>
        </form>

        <div className="auth-modal-footer">
          <button
            type="button"
            className="auth-toggle-button"
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setFormData({ username: '', email: '', password: '' });
            }}
          >
            {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;

